import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Linkedin, ArrowUpRight, Phone } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Logo } from "@/components/common/Logo";
import { SERVICES } from "@/data/services";

export function Footer() {
  const { lang, t, ml } = useI18n();
  const L = (p) => `/${lang}${p}`;
  const year = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="relative border-t border-white/10 bg-black">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Logo />
            <p className="mt-5 text-white/50 text-sm max-w-xs leading-relaxed">{t("footer.tagline")}</p>
            <div className="mt-6 flex items-center gap-3">
              <a href="https://www.linkedin.com" target="_blank" rel="noreferrer" data-testid="footer-linkedin" aria-label="LinkedIn"
                 className="p-2.5 rounded-full border border-white/15 text-white/70 hover:text-neon hover:border-neon/50 transition-colors">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="mailto:info@hosys.es" data-testid="footer-email" aria-label="Email"
                 className="p-2.5 rounded-full border border-white/15 text-white/70 hover:text-neon hover:border-neon/50 transition-colors">
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">{t("footer.company")}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to={L("/solutions")} className="text-white/70 hover:text-white transition-colors">{t("nav.solutions")}</Link></li>
              <li><Link to={L("/sectors")} className="text-white/70 hover:text-white transition-colors">{t("nav.sectors")}</Link></li>
              <li><Link to={L("/projects")} className="text-white/70 hover:text-white transition-colors">{t("nav.projects")}</Link></li>
              <li><Link to={L("/hosys-ai")} className="text-white/70 hover:text-white transition-colors">{t("nav.hosysAi")}</Link></li>
              <li><Link to={L("/insights")} className="text-white/70 hover:text-white transition-colors">{t("nav.insights")}</Link></li>
              <li><Link to={L("/company")} className="text-white/70 hover:text-white transition-colors">{t("nav.company")}</Link></li>
              <li><Link to={L("/contact")} className="text-white/70 hover:text-white transition-colors">{t("nav.contact")}</Link></li>
              <li><Link to={L("/client")} className="text-white/70 hover:text-white transition-colors">{t("nav.clientArea")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">{t("footer.solutions")}</h4>
            <ul className="space-y-3 text-sm">
              {SERVICES.slice(0, 5).map((s) => (
                <li key={s.slug}>
                  <Link to={L(`/services/${s.slug}`)} className="text-white/70 hover:text-white transition-colors">{ml(s.title)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">{t("nav.contact")}</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-neon" /> Reus, Catalunya</li>
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-neon" /> 696 99 08 79</li>
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-neon" /> info@hosys.es</li>
            </ul>
            <Link to={L("/demo")} data-testid="footer-cta"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-neon hover:text-white transition-colors">
              {t("nav.demo")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">© {year} HO-SYS. {t("footer.rights")}</p>
            <div className="flex items-center gap-6 text-xs text-white/40">
              <Link to={L("/privacy")} data-testid="footer-privacy" className="hover:text-white/70 transition-colors">{t("footer.privacy")}</Link>
              <Link to={L("/legal")} data-testid="footer-legal" className="hover:text-white/70 transition-colors">{t("footer.terms")}</Link>
            </div>
          </div>
          <p className="text-[11px] text-white/25 leading-relaxed">
            Hosys Advanced Digital Technologies SLU · CIF B88719794 · Inscrita al Registre Mercantil, Full T-00066453 · Foli electrònic IRUS: 1000474955269
          </p>
        </div>
      </div>
    </footer>
  );
}
